/** FARBAR contract modal (Tom 2026-08-12). Opens from a property; prefills the
 * terms from the deal + org defaults; "sign it" toggles the typed signature;
 * Generate downloads the filled PDF. Admins also get an inline setup section:
 * upload the blank FARBAR PDF, upload a signature font, and save defaults.
 * No BoldSign — the backend fills Tom's own uploaded form and streams a PDF. */
import { useEffect, useRef, useState } from "react";
import {
  type FarbarValues, type FarbarStatus, type FarbarDefaults, type FarbarSeed,
  farbarStatus, getFarbarDefaults, saveFarbarDefaults,
  uploadFarbarTemplate, uploadFarbarSignFont, generateFarbar, downloadBlob,
} from "./api";

type Seed = FarbarSeed;   // built by farbarSeedFor() in ./api

const TERM_FIELDS: { key: keyof FarbarValues; label: string; wide?: boolean; area?: boolean }[] = [
  { key: "sellerName", label: "Seller name", wide: true },
  { key: "buyerCompany", label: "Buyer (your company)", wide: true },
  { key: "streetAddress", label: "Property address", wide: true },
  { key: "county", label: "County" },
  { key: "parcelId", label: "Parcel / Tax ID #" },
  { key: "purchasePrice", label: "Purchase price $" },
  { key: "initialDeposit", label: "Initial deposit $" },
  { key: "titleCompany", label: "Title / escrow company", wide: true },
  { key: "titleAddress", label: "Title company address", wide: true },
  { key: "titlePhone", label: "Title phone" },
  { key: "titleEmail", label: "Title email" },
  { key: "closingDate", label: "Closing date" },
  { key: "inspectionDays", label: "Inspection period (days)" },
  { key: "listingAssociate", label: "Listing sales associate" },
  { key: "listingBroker", label: "Listing broker" },
  { key: "additionalTerms", label: "Additional terms", wide: true, area: true },
];

// checkbox terms Tom wants on by default for his cash wholesale deals
const CHECK_FIELDS: { key: keyof FarbarValues; label: string }[] = [
  { key: "cashSale", label: "Cash transaction — no financing contingency (¶8a)" },
  { key: "buyerClosingAgent", label: "Buyer designates Closing Agent & pays owner's title policy (¶9c-ii)" },
  { key: "mayAssign", label: "Buyer may assign, but not released from liability (¶7 assignability)" },
];

export function FarbarModal({ token, isAdmin, seed, onClose }: {
  token: string | null;
  isAdmin: boolean;
  seed: Seed;
  onClose: () => void;
}) {
  const [status, setStatus] = useState<FarbarStatus | null>(null);
  const [vals, setVals] = useState<FarbarValues>({});
  const [sign, setSign] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [defaults, setDefaults] = useState<FarbarDefaults>({ defaults: {}, signName: "", signInitials: "" });
  const tplRef = useRef<HTMLInputElement>(null);
  const fontRef = useRef<HTMLInputElement>(null);

  // load status + defaults, then seed the form (defaults first, deal overrides)
  useEffect(() => {
    let alive = true;
    void (async () => {
      const [st, d] = await Promise.all([farbarStatus(token), getFarbarDefaults(token)]);
      if (!alive) return;
      setStatus(st);
      setDefaults(d);
      const today = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
      setVals({
        // cash wholesale defaults — ticked unless the org defaults say otherwise
        cashSale: "1",
        buyerClosingAgent: "1",
        mayAssign: "1",
        ...d.defaults,
        ...(seed.sellerName ? { sellerName: seed.sellerName } : {}),
        ...(seed.streetAddress ? { streetAddress: seed.streetAddress } : {}),
        ...(seed.county ? { county: seed.county } : {}),
        ...(seed.parcelId ? { parcelId: seed.parcelId } : {}),
        ...(seed.purchasePrice ? { purchasePrice: seed.purchasePrice } : {}),
        ...(seed.listingAssociate ? { listingAssociate: seed.listingAssociate } : {}),
        ...(seed.listingBroker ? { listingBroker: seed.listingBroker } : {}),
        signDate: today,
      });
    })();
    return () => { alive = false; };
  }, [token, seed.sellerName, seed.streetAddress, seed.county, seed.parcelId,
      seed.purchasePrice, seed.listingAssociate, seed.listingBroker]);

  const set = (k: keyof FarbarValues, v: string) => setVals((p) => ({ ...p, [k]: v }));

  const generate = async () => {
    if (busy) return;
    setBusy(true); setNote(null);
    try {
      const nm = "FARBAR-" + ((vals.streetAddress || "contract").split(",")[0] ?? "contract").replace(/[^\w-]+/g, "-").slice(0, 40);
      const blob = await generateFarbar(token, vals, sign, nm);
      downloadBlob(blob, nm + ".pdf");
      setNote("Downloaded ✓ — ready to send.");
    } catch (e) {
      setNote((e as { detail?: string; message?: string })?.detail ?? (e as Error)?.message ?? "Generate failed");
    } finally {
      setBusy(false);
    }
  };

  const doUpload = async (kind: "tpl" | "font", file: File) => {
    setBusy(true); setNote(null);
    try {
      if (kind === "tpl") {
        const r = await uploadFarbarTemplate(token, file);
        setNote(`Template saved — ${(r["fieldCount"] as number) ?? 0} fields, ${(r["pages"] as number) ?? "?"} pages ✓`);
      } else {
        await uploadFarbarSignFont(token, file);
        setNote("Signature font saved ✓");
      }
      setStatus(await farbarStatus(token));
    } catch (e) {
      setNote((e as { detail?: string; message?: string })?.detail ?? (e as Error)?.message ?? "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const saveDefaults = async () => {
    setBusy(true); setNote(null);
    try {
      // defaults = the CURRENT term values minus the deal-specific ones.
      // Title company + cash/closing-agent checkboxes DO persist as defaults;
      // seller/address/parcel/price and the Zillow-sourced listing broker/
      // associate are per-deal and stripped.
      const d: FarbarValues = { ...vals };
      delete d.sellerName; delete d.streetAddress; delete d.county;
      delete d.parcelId; delete d.purchasePrice; delete d.signDate;
      delete d.listingAssociate; delete d.listingBroker;
      await saveFarbarDefaults(token, { ...defaults, defaults: d });
      setNote("Defaults saved ✓ — they'll prefill every contract.");
    } catch (e) {
      setNote((e as { detail?: string; message?: string })?.detail ?? (e as Error)?.message ?? "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const noTemplate = status !== null && !status.hasTemplate;

  return (
    <div className="modalscrim" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal fbmodal" role="dialog" aria-modal="true">
        <div className="modalhd">
          <h2>📄 FARBAR Contract</h2>
          <button className="modalx" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {noTemplate ? (
          <div className="fbwarn">
            No FARBAR form uploaded yet.{isAdmin ? " Upload your blank “AS IS” PDF in Setup below." : " Ask an admin to upload the blank “AS IS” PDF."}
          </div>
        ) : null}

        <div className="fbgrid">
          {TERM_FIELDS.map((f) => (
            <label key={f.key} className={f.wide ? "wide" : ""}>
              {f.label}
              {f.area ? (
                <textarea rows={2} value={vals[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} />
              ) : (
                <input value={vals[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} />
              )}
            </label>
          ))}
        </div>

        <div className="fbchecks">
          {CHECK_FIELDS.map((c) => (
            <label key={c.key} className="fbcheck">
              <input type="checkbox" checked={(vals[c.key] ?? "") === "1"}
                onChange={(e) => set(c.key, e.target.checked ? "1" : "")} />
              <span>{c.label}</span>
            </label>
          ))}
        </div>

        <label className="fbsign">
          <input type="checkbox" checked={sign} onChange={(e) => setSign(e.target.checked)} />
          <span>Sign it — stamp my typed signature{defaults.signName ? ` (${defaults.signName})` : ""} &amp; initials on the buyer lines</span>
        </label>

        <div className="modalacts">
          <button className="btn primary" disabled={busy || noTemplate} onClick={() => void generate()}>
            {busy ? "Working…" : "Generate & download"}
          </button>
          <button className="btn" onClick={onClose}>Close</button>
          {isAdmin ? <button className="btn ghost" onClick={() => setSetupOpen((v) => !v)}>{setupOpen ? "Hide setup" : "Setup ▾"}</button> : null}
        </div>
        {note ? <div className="fbnote">{note}</div> : null}

        {isAdmin && setupOpen ? (
          <div className="fbsetup">
            <div className="fbsetrow">
              <span>Blank FARBAR PDF: <b>{status?.hasTemplate ? `loaded (${status.nFields} fields)` : "none"}</b></span>
              <button className="btn slim" disabled={busy} onClick={() => tplRef.current?.click()}>Upload PDF</button>
              <input ref={tplRef} type="file" accept="application/pdf" hidden
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void doUpload("tpl", f); e.target.value = ""; }} />
            </div>
            <div className="fbsetrow">
              <span>Signature font: <b>{status?.hasSignFont ? "custom loaded" : "default (Times-Italic)"}</b></span>
              <button className="btn slim" disabled={busy} onClick={() => fontRef.current?.click()}>Upload .ttf</button>
              <input ref={fontRef} type="file" accept=".ttf,.otf,font/ttf,font/otf" hidden
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void doUpload("font", f); e.target.value = ""; }} />
            </div>
            <div className="fbsetrow">
              <label className="fbsm">Signature name<input value={defaults.signName} onChange={(e) => setDefaults((d) => ({ ...d, signName: e.target.value }))} /></label>
              <label className="fbsm">Initials<input value={defaults.signInitials} onChange={(e) => setDefaults((d) => ({ ...d, signInitials: e.target.value }))} /></label>
            </div>
            <button className="btn slim primary" disabled={busy} onClick={() => void saveDefaults()}>Save current terms as defaults</button>
            <p className="fbhint">Defaults prefill every new contract (buyer company, title company, standard inspection days, other terms). Deal-specific fields (seller, address, parcel, price) always come from the property.</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
